import React, { useState } from "react";
import { Row, Tag, Typography, Space, DatePicker, Button } from "antd";
import moment from "moment";
import metaData from "../../../data/metaData";
import ClientInfo from "./common/ClientInfo";
import PaidInfo from "./common/PaidInfo";
import AssistantInfo from "./common/AssistantInfo";
import Meta from "../../Meta";
import PaymentInfo from "./common/PaymentInfo";
import PaymentLogo from "./common/PaymentLogo";
import { formatTime } from "../../../utils/time";
import ShopLogo from "../../Shop/Logo";
import { getSellingPrice } from "../../../utils/product";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const CoinInfo = ({ record }) => {
  return (
    <Meta
      image={
        <ShopLogo
          style={{ width: 50, height: 50, marginRight: 10, borderRadius: "5px" }}
          logo={record?.logo}
          brandLogo={record?.brand?.logo}
        />
      }
      title={record?.brand?.name || "--"}
      description={record?.period?.name ? `${record.period.name}` : "--"}
    />
  );
};

const formatNumber = (num) => {
  if (typeof num !== "number" || isNaN(num)) return "--"; // Handle invalid numbers
  return parseFloat(num.toFixed(2)); // Ensure max 2 decimals
};

/**
 * TableColumns component generates column configuration for a table based on the specified order and translations.
 *
 * @param {Array} columnOrder - The order of columns to display in the table.
 * @param {Object} translations - The translations for column titles, passed from the parent component.
 * @param {Object} filterOptions - A list of filter options, passed from the parent component.
 */
const TableColumns = ({ columnOrder, translations, filterOptions }) => {
  const {
    recordIdTranslation,
    timeTranslation,
    statusTranslation,
    assistantTranslation,
    consumerTranslation,
    totalAmountTranslation,
  } = translations || {};

  const { shopFilterOptions } = filterOptions || {};

  const columnConfig = {
    id: {
      title: recordIdTranslation,
      dataIndex: "id",
      key: "id",
      width: 175,
      render: (text, record) => <div>{record.id ?? "--"}</div>,
    },
    time: {
      title: timeTranslation,
      dataIndex: "time",
      key: "time",
      width: 200,
      render: (text, record) => formatTime(record?.time),
      sorter: (a, b) => moment(a.time).unix() - moment(b.time).unix(),
    },
    status: {
      title: statusTranslation,
      dataIndex: "status",
      key: "status",
      width: 125,
      ellipsis: true,
      render: (text) => (
        <Tag style={{ fontSize: "15px" }} color="success">
          Success
        </Tag>
      ),
    },
    shop: {
      title: "Location",
      dataIndex: "shop",
      key: "shop",
      width: 300,
      ellipsis: true,
      render: (text, record) =>
        !record?.shop ? null : (
          <Meta
            image={
              <img
                style={{ width: 50, height: 50, objectFit: "cover", borderRadius: "5px" }}
                src={metaData.blobPreURL + "clientUpload-" + record?.shop?.logo + ".jpeg" + metaData.blobPostURL}
              />
            }
            title={
              <Text style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {record?.shop?.name || "--"}
              </Text>
            }
            maxWidth={200}
          />
        ),
      sorter: (a, b) => a.shop?.name.localeCompare(b.shop?.name),
      filters: shopFilterOptions,
      onFilter: (value, record) => record?.shop?.name === value,
    },
    assistant: {
      title: assistantTranslation,
      dataIndex: "assistant",
      key: "assistant",
      width: 150,
      ellipsis: true,
      render: (text, record) => (
        <>{record?.payment?.method === "online" ? null : <AssistantInfo assistant={record.assistant} />}</>
      ),
      sorter: (a, b) => a.assistant?.id.localeCompare(b.assistant?.id),
    },
    client: {
      title: consumerTranslation,
      dataIndex: "consumer",
      key: "consumer",
      width: 200,
      ellipsis: true,
      render: (text, record) => <ClientInfo user={record.client} />,
      sorter: (a, b) => a.client.id.localeCompare(b.client.id),
    },
    from: {
      title: "From",
      dataIndex: "consumer",
      key: "consumer",
      width: 200,
      ellipsis: true,
      render: (text, record) => <ClientInfo user={record.from} />,
      sorter: (a, b) => a.from.id.localeCompare(b.from.id),
    },
    to: {
      title: "To",
      dataIndex: "consumer",
      key: "consumer",
      width: 200,
      ellipsis: true,
      render: (text, record) => <ClientInfo user={record.to} />,
      sorter: (a, b) => a.to.id.localeCompare(b.to.id),
    },
    txTotalAmount: {
      title: totalAmountTranslation,
      dataIndex: "txTotalAmount",
      key: "txTotalAmount",
      width: 200,
      ellipsis: true,
      render: (text, record) =>
        record?.payment?.method == "physical" ? (
          <PaidInfo paid={record.amount} giftCardPaid={record?.giftCardPaid} />
        ) : (
          <div>{formatNumber(record?.amount)}</div>
        ),
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
    },
    txTotalQty: {
      title: "Total Qty",
      dataIndex: "txTotalQty",
      key: "txTotalQty",
      width: 100,
      ellipsis: true,
      render: (text, record) => (
        <Row style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
          <a>{formatNumber(record.items.reduce((total, item) => total + item.qty, 0))}</a>
        </Row>
      ),
    },
    payment: {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 175,
      ellipsis: true,
      render: (text, record) => (
        <Space>
          <PaymentLogo payment={record.payment} />
          <PaymentInfo payment={record.payment} />
        </Space>
      ),
      sorter: (a, b) => a.payment.method.localeCompare(b.payment.method),
      filters: [
        { text: "Paid at Shop", value: "physical" },
        { text: "Coin Redeem", value: "estamp" },
        { text: "Promotion Code", value: "promotionCode" },
        { text: "Welcome", value: "welcome" },
        { text: "Referrer", value: "referrer" },
        { text: "Referee", value: "referee" },
      ],
      onFilter: (value, record) => record.payment.method === value,
    },
    //
    product: {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (text, record) => (
        <Meta
          image={
            <img
              style={{ marginRight: 10, borderRadius: "5px" }}
              width={50}
              height={50}
              src={metaData.blobPreURL + "clientUpload-" + record?.image + ".jpeg" + metaData.blobPostURL}
            />
          }
          title={record?.name || "--"}
          description={record?.giftCard?.id ? `ID: ${record.giftCard.id}` : `ID: ${record.id}`}
        />
      ),
    },
    distributeId: {
      title: "Distribute Record ID",
      dataIndex: "distributeLogId",
      key: "distributeLogId",
      width: 175,
    },
    value: {
      title: "Value",
      dataIndex: "price",
      key: "price",
    },
    price: {
      title: "Selling Price",
      dataIndex: "price",
      key: "price",
      render: (text, record) => {
        if (record?.isFree) {
          return (
            <>
              <Row>
                <Text>Free</Text>
              </Row>

              <Row>
                <Tag color="magenta">
                  Buy {record?.discountMethod?.trigger?.value} Get {record?.discountMethod?.reward?.value} Free
                </Tag>
              </Row>
            </>
          );
        } else {
          return <Text>{getSellingPrice(record)}</Text>;
        }
      },
    },
    qty: {
      title: "Quantity",
      dataIndex: "qty",
      key: "qty",
    },
    totalAmount: {
      title: "Total Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => {
        if (record?.isFree) {
          return <Text>Free</Text>;
        } else {
          let price = getSellingPrice(record);
          let amount = price * record?.qty;
          return <Text>{amount}</Text>;
        }
      },
    },
    productType: {
      title: "Product Type",
      dataIndex: "type",
      key: "type",
      render: (text, record) => {
        return <Text>{!record?.giftCard ? "Voucher" : "Gift Card"}</Text>;
      },
    },
    orderTotal: {
      title: "Order Total",
      dataIndex: "orderTotal",
      key: "orderTotal",
      render: (text, record) => {
        return <Text>{formatNumber(record?.giftCard?.orderTotal)}</Text>;
      },
    },
    spent: {
      title: "Spent",
      dataIndex: "spent",
      key: "spent",
      render: (text, record) => {
        return <Text>{formatNumber(record?.giftCard?.spent)}</Text>;
      },
    },
    balance: {
      title: "Remain Balance",
      dataIndex: "balance",
      key: "balance",
      render: (text, record) => {
        return <Text>{formatNumber(record?.giftCard?.balance)}</Text>;
      },
    },
    //
    coinMethod: {
      title: "Method",
      dataIndex: "method",
      key: "method",
      width: 175,
      ellipsis: true,
      render: (text, record) => {
        const methodDisplayMap = {
          physical: "Pay 2 Earn",
          sale: "Paid at Shop",
          promotionCode: `Code: ${record?.code}`,
          referrer: `Referrer`,
          referee: `Referee`,
        };

        const displayMethod = methodDisplayMap[record?.method] || record?.method;
        return <Tag style={{ fontSize: "1rem" }}>{displayMethod}</Tag>;
      },
      sorter: (a, b) => a.method.localeCompare(b.method),
    },
    coinTransactionId: {
      title: "Related Record ID",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 200,
      ellipsis: true,
      render: (text, record) => <Text>{record?.transactionId}</Text>,
      sorter: (a, b) => parseFloat(a.method) - parseFloat(b.method),
    },
    coin: {
      title: "Coin",
      dataIndex: "coin",
      key: "coin",
      render: (text, record) => <CoinInfo record={record} />,
    },
    singleCoin: {
      title: "Coin",
      dataIndex: "coin",
      key: "coin",
      render: (text, record) => <CoinInfo record={record.coins?.[0]} />,
    },
    coinAmount: {
      title: "Coin Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => <div>{record.amount}</div>,
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
    },
  };

  // Generate columns based on the specified order
  const columns = columnOrder.map((colKey) => columnConfig[colKey]);

  // Return the columns as part of an object
  return { columns };
};

export default TableColumns;
